import { useCallback, useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
    type AutocompleteRenderInputParams,
    type TextFieldProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PlayIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import BeachIcon from '@mui/icons-material/BeachAccess';
import SickIcon from '@mui/icons-material/Sick';
import CompassionIcon from '@mui/icons-material/VolunteerActivism';
import PayIcon from '@mui/icons-material/RequestQuote';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import { ApiError } from '../../lib/api';
import { MapLink } from '../../components/MapLink';
import { ACCENTS } from '../../lib/theme';
import { diffDays, formatAuRange, formatShort, today } from '../../lib/dates';
import {
    addPayNote,
    addTradeSchool,
    fetchHome,
    fetchLeaveRequests,
    LEAVE_LABELS,
    requestLeave,
    startShift,
    stopShift,
    type EmployeeHome,
    type ForgottenShift,
    type MyBooking,
    type MyLeaveRequest,
    type RequestableLeave,
} from '../../lib/employee';
import { SmileyFace } from '../../components/employee/SmileyFace';
import { Toast, type ToastMessage } from '../../components/Toast';
import { EmployeeShell, FullWidthButton, SheetDialog } from './shell';

/**
 * The employee home screen — a port of
 * `views/backend/employeedashboard/backend_dashboard.php`, reorganised around
 * what actually happens on a shift.
 *
 * ── THE ROSTER LEADS ────────────────────────────────────────────────────────
 * The legacy page opened with two empty <select> elements: pick a client, then
 * pick a site, then Start. But on almost every shift the job is already on the
 * calendar — somebody in the office rostered this person onto it. So today's
 * bookings come first, each with its own Start, and the common case is one tap.
 *
 * The pickers are still here, behind "Find another job", for the case they were
 * really for: being sent somewhere that is not on the roster. That is the
 * exception, so it is one tap away rather than in the way.
 *
 * Both pickers are typeaheads. A native <select> of 39 clients is a picker
 * wheel scrolled with a thumb, on a site; three letters is faster and does not
 * depend on knowing the exact trading name.
 */

type Busy = 'idle' | 'starting' | 'stopping';

/**
 * The three leave types, each with its own colour and icon.
 *
 * They were all purple with the same umbrella icon, which made three adjacent
 * full-width buttons distinguishable only by reading them — and a mis-tap here
 * files the wrong kind of leave against someone's entitlements, which the
 * office then has to unpick. Colour and shape now differ before the text is
 * read: holiday teal, sick amber, compassionate pink.
 *
 * Amber rather than red for sick leave, because red is Stop everywhere else in
 * this app and should keep meaning exactly one thing.
 */
const LEAVE_CHOICES = [
    { type: 'annual_leave', colour: ACCENTS.teal, icon: <BeachIcon /> },
    { type: 'sick_leave', colour: ACCENTS.amber, icon: <SickIcon /> },
    { type: 'compassionate_leave', colour: ACCENTS.pink, icon: <CompassionIcon /> },
] as const satisfies readonly { type: RequestableLeave; colour: string; icon: React.ReactNode }[];

/**
 * Widen Autocomplete's renderInput params to what TextField accepts.
 *
 * A types-only mismatch in MUI v6 under `exactOptionalPropertyTypes`: the params
 * type several props as `X | undefined` (`size`, `InputLabelProps.className`,
 * and others behind them) where TextField's props require `X`. At runtime these
 * are the values MUI itself produces for its own TextField, so nothing is
 * unsound — but each property only surfaces once the one before it is silenced,
 * so fixing them individually is whack-a-mole.
 *
 * One named function at the single seam where it matters, so it is greppable and
 * can be deleted when MUI's types are fixed. The alternative is turning
 * exactOptionalPropertyTypes off for the whole app, which would lose a real
 * check everywhere else to satisfy one library's typings.
 */
function asTextFieldProps(params: AutocompleteRenderInputParams): TextFieldProps {
    return params as TextFieldProps;
}

/**
 * Strip the `key` MUI injects into renderOption's props.
 *
 * Two things go wrong without this. React 19 warns when a props object still
 * containing `key` is spread into JSX, and MUI types that `key` as `any`, so
 * destructuring it out trips no-unsafe-assignment. Removed here once; the
 * caller supplies a key it actually controls.
 */
function withoutKey(props: HTMLAttributes<HTMLLIElement>): HTMLAttributes<HTMLLIElement> {
    const rest: Record<string, unknown> = { ...props };
    delete rest['key'];
    return rest;
}

/**
 * How long this person has been on site, as H:MM:SS.
 *
 * SECONDS ARE SHOWN ON PURPOSE. An earlier version ticked once a second but
 * rendered only whole minutes, so the figure sat unchanged for a minute at a
 * time and read as a stopped clock — which is exactly how it was reported.
 * Either the display carries seconds or the tick should be a minute; showing a
 * coarser unit than you update is the worst of both.
 *
 * Both sides are compared as Melbourne wall-clock, never as instants. The
 * stored start is a Melbourne date and time; `new Date('2026-09-24T07:00:00')`
 * would parse it in the PHONE's timezone, so a phone that has travelled — or
 * one whose timezone is simply set wrong, which happens — would show an
 * elapsed figure hours out. Same reasoning as lib/dates.ts.
 */
/**
 * Told on screen, because the hours were not what they clocked. Longer on
 * screen than a success toast — it asks them to do something if it is wrong.
 */
function forgottenToast(f: ForgottenShift): ToastMessage {
    return {
        message:
            `Your shift on ${formatShort(f.workDate)} wasn't clocked off, so it was closed at standard hours ` +
            `(${f.startTime.slice(0, 5)}–${f.finishTime.slice(0, 5)}, ${f.hours.toFixed(2)} h). Tell the office if that's wrong.`,
        severity: 'warning',
    };
}

function elapsedSince(workDate: string, startTime: string | null, now: Date): string {
    if (startTime === null) {
        return '—';
    }
    const here = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Australia/Melbourne',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const part = (type: Intl.DateTimeFormatPartTypes): string => here.find((p) => p.type === type)?.value ?? '00';
    const nowDate = `${part('year')}-${part('month')}-${part('day')}`;
    const nowSeconds = Number(part('hour')) * 3600 + Number(part('minute')) * 60 + Number(part('second'));

    const [startHour = '0', startMinute = '0', startSecond = '0'] = startTime.split(':');
    const startSeconds = Number(startHour) * 3600 + Number(startMinute) * 60 + Number(startSecond);

    const total = Math.max(0, diffDays(workDate, nowDate) * 86_400 + nowSeconds - startSeconds);
    const hh = Math.floor(total / 3600);
    const mm = Math.floor((total % 3600) / 60);
    const ss = total % 60;
    return `${String(hh)}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/**
 * The running shift clock.
 *
 * Its own component so the one-second tick re-renders these few characters
 * rather than the whole page — the parent holds the roster, the pickers and
 * four dialogs, and re-rendering all of it every second on a phone is heat and
 * battery for nothing.
 *
 * Also recomputes on `visibilitychange`: browsers throttle or suspend timers in
 * a backgrounded tab, and a phone coming out of a pocket should show the right
 * time immediately rather than whatever it was when the screen locked.
 */
function ShiftClock({ workDate, startTime }: { workDate: string; startTime: string | null }): React.JSX.Element {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const tick = (): void => {
            setNow(new Date());
        };
        const timer = setInterval(tick, 1000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, []);

    return (
        <Typography
            sx={{
                fontSize: '1.6rem',
                fontWeight: 800,
                color: ACCENTS.green,
                lineHeight: 1,
                // Tabular figures: without them the width jumps on every tick
                // as the digits change and the whole row jitters.
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {elapsedSince(workDate, startTime, now)}
        </Typography>
    );
}

/**
 * The friendly face at the top right of My Day.
 *
 * Green and settled once the person is clocked on; amber and gently breathing
 * before that, to draw the eye toward starting the day. The animation is the
 * only thing on the screen that moves on its own, so it is slow and small —
 * and it stops entirely for anyone who has asked their device to reduce motion,
 * which on a page people open dozens of times a day is the difference between
 * a nudge and an irritation.
 */
function DayMood({ started }: { started: boolean }): React.JSX.Element {
    return (
        <Box
            aria-hidden
            sx={{
                flexShrink: 0,
                display: 'flex',
                lineHeight: 0,
                ...(started
                    ? {}
                    : {
                          animation: 'dayMoodBreathe 2.6s ease-in-out infinite',
                          '@keyframes dayMoodBreathe': {
                              '0%, 100%': { transform: 'scale(1)' },
                              '50%': { transform: 'scale(1.08)' },
                          },
                          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                      }),
            }}
        >
            <SmileyFace colour={started ? ACCENTS.green : ACCENTS.amber} size={42} />
        </Box>
    );
}

/** A full-width button that opens and closes a panel attached beneath it. */
function Disclosure({
    label,
    icon,
    colour,
    open,
    onToggle,
    children,
}: {
    label: string;
    icon: React.ReactNode;
    colour: string;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}): React.JSX.Element {
    return (
        <Box>
            <FullWidthButton
                colour={colour}
                startIcon={icon}
                onClick={onToggle}
                // Square off the bottom while open so the panel below reads as
                // the same object rather than a second floating card.
                sx={open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {label}
                    <ExpandIcon
                        sx={{
                            fontSize: 20,
                            transition: 'transform 0.2s',
                            transform: open ? 'rotate(180deg)' : 'none',
                        }}
                    />
                </Box>
            </FullWidthButton>
            <Collapse in={open}>
                <Paper
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        border: '1.5px solid',
                        borderColor: alpha(colour, 0.5),
                        borderTop: 'none',
                        bgcolor: alpha(colour, 0.05),
                    }}
                >
                    {children}
                </Paper>
            </Collapse>
        </Box>
    );
}

/**
 * One rostered booking, with its own Start.
 *
 * A booking with no job is leave or other non-job time — trade school, a public
 * holiday. There is nothing to clock on to, so it is shown for context and
 * carries no button rather than a button that would fail.
 */
function BookingStartCard({
    booking,
    disabled,
    starting,
    onStart,
}: {
    booking: MyBooking;
    disabled: boolean;
    starting: boolean;
    onStart: () => void;
}): React.JSX.Element {
    const isJob = booking.jobId !== null;
    const accent = isJob ? ACCENTS.green : ACCENTS.purple;
    const title =
        booking.categoryName ??
        (booking.siteAddress !== null && booking.siteAddress !== ''
            ? booking.siteAddress
            : `Job ${booking.jobNumber ?? ''}`);

    return (
        <Paper
            sx={{
                p: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(accent, 0.35),
                bgcolor: 'background.paper',
            }}
        >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                    {booking.clientName !== null ? (
                        <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}
                        >
                            {booking.clientName}
                        </Typography>
                    ) : null}
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{title}</Typography>
                </Box>
                {isJob ? <MapLink address={booking.siteAddress} /> : null}
            </Stack>

            {/* Times only. The job number meant nothing to the person standing
                at the gate — they know the site by its address — and it was
                competing with the one chip that does matter. It is still the
                title's fallback for the rare job with no address recorded. */}
            {booking.startTime !== null || booking.endTime !== null ? (
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                    <Chip
                        size="small"
                        label={`${booking.startTime?.slice(0, 5) ?? '—'} – ${booking.endTime?.slice(0, 5) ?? '—'}`}
                        sx={{ bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 600 }}
                    />
                </Stack>
            ) : null}

            {isJob ? (
                <FullWidthButton
                    colour={ACCENTS.green}
                    startIcon={<PlayIcon />}
                    disabled={disabled}
                    onClick={onStart}
                    sx={{ mt: 1.5 }}
                >
                    {starting ? 'Starting…' : 'Start'}
                </FullWidthButton>
            ) : null}
        </Paper>
    );
}

export function MyDay(): React.JSX.Element {
    const navigate = useNavigate();
    const [home, setHome] = useState<EmployeeHome | null>(null);
    const [leave, setLeave] = useState<readonly MyLeaveRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState<Busy>('idle');
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [clientId, setClientId] = useState<number | ''>('');
    const [jobId, setJobId] = useState<number | ''>('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [leavePanelOpen, setLeavePanelOpen] = useState(false);

    const [stopOpen, setStopOpen] = useState(false);
    const [stopNote, setStopNote] = useState('');
    const [leaveType, setLeaveType] = useState<RequestableLeave | null>(null);
    const [leaveFrom, setLeaveFrom] = useState(today());
    const [leaveTo, setLeaveTo] = useState(today());
    const [tradeOpen, setTradeOpen] = useState(false);
    const [tradeFrom, setTradeFrom] = useState(today());
    const [tradeTo, setTradeTo] = useState(today());
    const [payOpen, setPayOpen] = useState(false);
    const [payDate, setPayDate] = useState(today());
    const [payNote, setPayNote] = useState('');

    const load = useCallback(async (): Promise<void> => {
        try {
            const [h, l] = await Promise.all([fetchHome(), fetchLeaveRequests()]);
            setHome(h);
            setLeave(l);
            if (h.closedForgotten !== null) {
                setToast(forgottenToast(h.closedForgotten));
            }
            setError(null);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    /**
     * What the job picker offers: this client's jobs once one is chosen, and
     * every active job before that. There are 99, which is a short list to
     * search and a long one to scroll — hence the typeahead.
     */
    const jobOptions = useMemo(() => {
        if (home === null) {
            return [];
        }
        return clientId === '' ? home.jobs : home.jobs.filter((j) => j.clientId === clientId);
    }, [home, clientId]);

    const selectedClient = useMemo(() => home?.clients.find((c) => c.id === clientId) ?? null, [home, clientId]);
    const selectedJob = useMemo(() => home?.jobs.find((j) => j.id === jobId) ?? null, [home, jobId]);

    /** For the second line of each job option. */
    const clientNameById = useMemo(() => new Map((home?.clients ?? []).map((c) => [c.id, c.name])), [home]);

    function report(err: unknown, fallback: string): void {
        setToast({
            message: err instanceof ApiError ? err.message : err instanceof Error ? err.message : fallback,
            severity: 'error',
        });
    }

    async function onStart(input: { jobId: number } | { tradeSchool: true }): Promise<void> {
        setBusy('starting');
        try {
            const result = await startShift(input);
            await load();
            if (result.closedForgotten !== null) {
                setToast(forgottenToast(result.closedForgotten));
            }
            setSearchOpen(false);
            // The shift has started either way. A SWMS that is due sends the
            // person straight to it, which is what the legacy redirect did.
            if (result.swms.required && result.swms.jobId !== null) {
                void navigate(`/my/swms/${String(result.swms.jobId)}`);
                return;
            }
            setToast({
                message: 'tradeSchool' in input ? 'Trade School started' : 'Clocked on',
                severity: 'success',
            });
        } catch (err: unknown) {
            report(err, 'Could not start your shift');
        } finally {
            setBusy('idle');
        }
    }

    async function onStop(): Promise<void> {
        setBusy('stopping');
        try {
            const result = await stopShift(stopNote.trim() === '' ? null : stopNote.trim());
            setStopOpen(false);
            setStopNote('');
            await load();
            if (result.autoClosed !== null) {
                setToast(forgottenToast(result.autoClosed));
                return;
            }
            const overtime = result.overtime15 + result.overtime2;
            setToast({
                message:
                    `Clocked off — ${result.totalHours.toFixed(2)} hours` +
                    (overtime > 0 ? `, ${overtime.toFixed(2)} overtime` : '') +
                    (result.lunchDeducted ? ', lunch deducted' : ''),
                severity: 'success',
            });
        } catch (err: unknown) {
            report(err, 'Could not stop your shift');
        } finally {
            setBusy('idle');
        }
    }

    async function onRequestLeave(): Promise<void> {
        if (leaveType === null) {
            return;
        }
        try {
            await requestLeave({ leaveType, startsOn: leaveFrom, endsOn: leaveTo });
            setLeaveType(null);
            setLeavePanelOpen(false);
            setLeave(await fetchLeaveRequests());
            setToast({ message: `${LEAVE_LABELS[leaveType] ?? 'Leave'} requested`, severity: 'success' });
        } catch (err: unknown) {
            report(err, 'Could not send your leave request');
        }
    }

    async function onAddTradeSchool(): Promise<void> {
        try {
            const result = await addTradeSchool(tradeFrom, tradeTo);
            setTradeOpen(false);
            setToast({
                message:
                    `${String(result.datesAdded)} day${result.datesAdded === 1 ? '' : 's'} added` +
                    (result.weekendsSkipped > 0 ? `, ${String(result.weekendsSkipped)} weekend days skipped` : '') +
                    (result.alreadyBooked > 0 ? `, ${String(result.alreadyBooked)} already booked` : ''),
                severity: 'success',
            });
        } catch (err: unknown) {
            report(err, 'Could not add Trade School');
        }
    }

    async function onAddPayNote(): Promise<void> {
        if (payNote.trim() === '') {
            setToast({ message: 'Write a note before sending it', severity: 'warning' });
            return;
        }
        try {
            await addPayNote({ noteDate: payDate, note: payNote.trim() });
            setPayOpen(false);
            setPayNote('');
            setToast({ message: 'Pay note sent', severity: 'success' });
        } catch (err: unknown) {
            report(err, 'Could not add your pay note');
        }
    }

    // No spinner: an empty shell for the beat before the data lands. `home` is
    // null both before the first response and after a failure, so the error
    // case below is gated on `loading` having finished.
    if (loading) {
        return <EmployeeShell title="My Day" />;
    }

    if (home === null) {
        return (
            <EmployeeShell title="My Day" subtitle="Something went wrong">
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error ?? 'Could not load your dashboard'}
                    </Typography>
                    <Button startIcon={<RefreshIcon />} onClick={() => void load()}>
                        Try again
                    </Button>
                </Paper>
            </EmployeeShell>
        );
    }

    const { profile, openShift, todayBookings } = home;
    const pending = leave.filter((l) => l.status === 'pending');
    const chosenLeave = LEAVE_CHOICES.find((c) => c.type === leaveType);

    return (
        <EmployeeShell
            title="My Day"
            subtitle={
                `${formatShort(home.today)} · ${openShift === null ? 'Not clocked on' : 'On site'}` +
                (home.hoursToday > 0 ? ` · ${home.hoursToday.toFixed(2)} h` : '')
            }
            action={<DayMood started={openShift !== null} />}
        >
            {/* ── The shift, when there is one ──────────────────────────── */}
            {openShift !== null ? (
                <Paper
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 3,
                        border: `2px solid ${alpha(ACCENTS.green, 0.7)}`,
                        background: `linear-gradient(135deg, ${alpha(ACCENTS.green, 0.16)} 0%, ${alpha(ACCENTS.green, 0.04)} 100%)`,
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Chip
                            size="small"
                            label={openShift.isTradeSchool ? 'Trade School' : 'Clocked on'}
                            sx={{ bgcolor: alpha(ACCENTS.green, 0.25), color: ACCENTS.green, fontWeight: 700 }}
                        />
                        <ShiftClock workDate={openShift.workDate} startTime={openShift.startTime} />
                    </Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                        {openShift.isTradeSchool
                            ? 'Trade School'
                            : (openShift.siteAddress ?? `Job ${String(openShift.jobNumber ?? '')}`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {openShift.clientName ?? '—'}
                        {openShift.startTime !== null ? ` · from ${openShift.startTime.slice(0, 5)}` : ''}
                    </Typography>

                    <FullWidthButton
                        colour={ACCENTS.red}
                        startIcon={<StopIcon />}
                        disabled={busy !== 'idle'}
                        onClick={() => {
                            setStopOpen(true);
                        }}
                        sx={{ mt: 2 }}
                    >
                        {busy === 'stopping' ? 'Stopping…' : 'Stop'}
                    </FullWidthButton>
                </Paper>
            ) : (
                <>
                    {/* ── Today's roster, with Start on each ─────────────── */}
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            mb: 1,
                        }}
                    >
                        Today&apos;s jobs
                    </Typography>

                    {todayBookings.length === 0 ? (
                        <Paper
                            sx={{
                                p: 2.5,
                                mb: 2,
                                borderRadius: 2,
                                border: '1px dashed',
                                borderColor: 'divider',
                                bgcolor: 'transparent',
                                textAlign: 'center',
                            }}
                        >
                            <Typography color="text.secondary" variant="body2">
                                Nothing on the calendar for you today.
                                <br />
                                Use Find another job below.
                            </Typography>
                        </Paper>
                    ) : (
                        <Stack spacing={1.25} sx={{ mb: 2 }}>
                            {todayBookings.map((booking) => (
                                <BookingStartCard
                                    key={booking.eventId}
                                    booking={booking}
                                    disabled={busy !== 'idle'}
                                    starting={busy === 'starting'}
                                    onStart={() => {
                                        if (booking.jobId !== null) {
                                            void onStart({ jobId: booking.jobId });
                                        }
                                    }}
                                />
                            ))}
                        </Stack>
                    )}

                    {/* ── The exception: sent somewhere else ─────────────── */}
                    <Box sx={{ mb: 2 }}>
                        <Disclosure
                            label="Find another job"
                            icon={<SearchIcon />}
                            colour={ACCENTS.blue}
                            open={searchOpen}
                            onToggle={() => {
                                setSearchOpen((o) => !o);
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                For a site you have not been rostered to.
                            </Typography>

                            <Autocomplete
                                options={home.clients}
                                getOptionLabel={(c) => c.name}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={selectedClient}
                                onChange={(_event, picked) => {
                                    setClientId(picked?.id ?? '');
                                    // The chosen job belongs to the old client.
                                    setJobId('');
                                }}
                                autoHighlight
                                // Dismisses the keyboard on pick, so Start is
                                // not hidden behind it.
                                blurOnSelect
                                openOnFocus
                                renderInput={(params) => (
                                    <TextField
                                        {...asTextFieldProps(params)}
                                        label="Client"
                                        placeholder="Type to search"
                                    />
                                )}
                                sx={{ mb: 1.5 }}
                                slotProps={{ listbox: { sx: { '& li': { minHeight: 48 } } } }}
                            />

                            {/* Not gated on picking a client first. Crews know
                                the site address, not always which builder it is
                                for, so every active job is searchable and
                                choosing one fills in its client. Picking a
                                client still narrows this list. */}
                            <Autocomplete
                                options={jobOptions}
                                getOptionLabel={(j) => (j.address === '' ? `Job ${String(j.jobNumber)}` : j.address)}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={selectedJob}
                                onChange={(_event, picked) => {
                                    setJobId(picked?.id ?? '');
                                    if (picked !== null && picked.clientId !== null) {
                                        setClientId(picked.clientId);
                                    }
                                }}
                                autoHighlight
                                blurOnSelect
                                openOnFocus
                                renderOption={(props, j) => (
                                    <Box component="li" key={j.id} {...withoutKey(props)} sx={{ minHeight: 48 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                {j.address === '' ? `Job ${String(j.jobNumber)}` : j.address}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {clientNameById.get(j.clientId ?? -1) ?? 'No client'} · Job{' '}
                                                {String(j.jobNumber)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...asTextFieldProps(params)}
                                        label="Job address"
                                        placeholder="Type to search"
                                    />
                                )}
                                noOptionsText="No matching active job"
                            />

                            <FullWidthButton
                                colour={ACCENTS.green}
                                startIcon={<PlayIcon />}
                                disabled={busy !== 'idle' || jobId === ''}
                                onClick={() => {
                                    if (jobId !== '') {
                                        void onStart({ jobId });
                                    }
                                }}
                                sx={{ mt: 2 }}
                            >
                                {busy === 'starting' ? 'Starting…' : 'Start'}
                            </FullWidthButton>
                        </Disclosure>
                    </Box>
                </>
            )}

            {/* ── Everything else ───────────────────────────────────────── */}
            <Stack spacing={1.25}>
                <FullWidthButton
                    colour={ACCENTS.blue}
                    startIcon={<CalendarIcon />}
                    onClick={() => void navigate('/my/calendar')}
                >
                    Calendar
                </FullWidthButton>

                <Disclosure
                    label="Request leave"
                    icon={<BeachIcon />}
                    colour={ACCENTS.purple}
                    open={leavePanelOpen}
                    onToggle={() => {
                        setLeavePanelOpen((o) => !o);
                    }}
                >
                    <Stack spacing={1.25}>
                        {LEAVE_CHOICES.map((choice) => (
                            <FullWidthButton
                                key={choice.type}
                                colour={choice.colour}
                                startIcon={choice.icon}
                                onClick={() => {
                                    setLeaveType(choice.type);
                                    setLeaveFrom(today());
                                    setLeaveTo(today());
                                }}
                            >
                                {LEAVE_LABELS[choice.type] ?? choice.type}
                            </FullWidthButton>
                        ))}
                    </Stack>
                </Disclosure>

                {profile.canTradeSchool && openShift === null ? (
                    <FullWidthButton
                        colour={ACCENTS.teal}
                        startIcon={<SchoolIcon />}
                        disabled={busy !== 'idle'}
                        onClick={() => void onStart({ tradeSchool: true })}
                    >
                        Start Trade School
                    </FullWidthButton>
                ) : null}

                {profile.canTradeSchool ? (
                    <FullWidthButton
                        colour={ACCENTS.teal}
                        startIcon={<SchoolIcon />}
                        onClick={() => {
                            setTradeOpen(true);
                        }}
                    >
                        Book Trade School days
                    </FullWidthButton>
                ) : null}

                {profile.canPayNotes ? (
                    <FullWidthButton
                        colour={ACCENTS.amber}
                        startIcon={<PayIcon />}
                        onClick={() => {
                            setPayOpen(true);
                        }}
                    >
                        Pay Notes
                    </FullWidthButton>
                ) : null}
            </Stack>

            {/* ── Leave already asked for ───────────────────────────────── */}
            {leave.length > 0 ? (
                <Paper sx={{ p: 2, mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                        My leave
                        {pending.length > 0 ? (
                            <Chip
                                size="small"
                                label={`${String(pending.length)} waiting`}
                                sx={{ ml: 1, bgcolor: alpha(ACCENTS.amber, 0.2), color: ACCENTS.amber }}
                            />
                        ) : null}
                    </Typography>
                    <Stack divider={<Divider flexItem />} spacing={1}>
                        {leave.slice(0, 8).map((l) => (
                            <Stack
                                key={l.id}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {LEAVE_LABELS[l.leaveType] ?? l.leaveType}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatAuRange(l.startsOn, l.endsOn)}
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={l.status}
                                    sx={{
                                        textTransform: 'capitalize',
                                        bgcolor: alpha(
                                            l.status === 'approved'
                                                ? ACCENTS.green
                                                : l.status === 'rejected'
                                                  ? ACCENTS.red
                                                  : ACCENTS.amber,
                                            0.2,
                                        ),
                                        color:
                                            l.status === 'approved'
                                                ? ACCENTS.green
                                                : l.status === 'rejected'
                                                  ? ACCENTS.red
                                                  : ACCENTS.amber,
                                        fontWeight: 700,
                                    }}
                                />
                            </Stack>
                        ))}
                    </Stack>
                </Paper>
            ) : null}

            {/* ── Stop, with the job note ───────────────────────────────── */}
            <SheetDialog
                open={stopOpen}
                title="Stop"
                onClose={() => {
                    setStopOpen(false);
                }}
            >
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add a job note before you clock off. Hours and overtime are worked out from your start and
                        finish times.
                        {/* Said out loud, because the legacy app deducted it silently and
                            people worked out the half hour was gone from their pay slip. */}
                        {profile.deductsLunch && openShift?.isTradeSchool !== true
                            ? ' A 30-minute lunch break comes off, once a day.'
                            : ''}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={4}
                        label="Job note"
                        placeholder="What was done today"
                        value={stopNote}
                        onChange={(e) => {
                            setStopNote(e.target.value);
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <FullWidthButton
                        colour={ACCENTS.red}
                        startIcon={<StopIcon />}
                        disabled={busy !== 'idle'}
                        onClick={() => void onStop()}
                    >
                        {busy === 'stopping' ? 'Stopping…' : 'Stop and submit'}
                    </FullWidthButton>
                </DialogActions>
            </SheetDialog>

            {/* ── Leave request dates ───────────────────────────────────── */}
            <Dialog
                open={leaveType !== null}
                fullWidth
                maxWidth="xs"
                onClose={() => {
                    setLeaveType(null);
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        // Carries the colour of whichever button was tapped, so
                        // the confirmation step cannot be misread as a
                        // different kind of leave.
                        color: chosenLeave?.colour ?? 'text.primary',
                    }}
                >
                    {chosenLeave?.icon}
                    Request {leaveType === null ? 'leave' : (LEAVE_LABELS[leaveType] ?? 'leave')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField
                            type="date"
                            label="From"
                            value={leaveFrom}
                            onChange={(e) => {
                                setLeaveFrom(e.target.value);
                                if (e.target.value > leaveTo) {
                                    setLeaveTo(e.target.value);
                                }
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label="To"
                            value={leaveTo}
                            inputProps={{ min: leaveFrom }}
                            onChange={(e) => {
                                setLeaveTo(e.target.value);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setLeaveType(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void onRequestLeave()}
                        {...(chosenLeave !== undefined && {
                            sx: {
                                bgcolor: chosenLeave.colour,
                                color: 'common.black',
                                '&:hover': { bgcolor: chosenLeave.colour, filter: 'brightness(1.1)' },
                            },
                        })}
                    >
                        Send request
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Trade School booking ──────────────────────────────────── */}
            <Dialog
                open={tradeOpen}
                fullWidth
                maxWidth="xs"
                onClose={() => {
                    setTradeOpen(false);
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Book Trade School</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Weekdays only — Saturdays and Sundays are skipped.
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            type="date"
                            label="From"
                            value={tradeFrom}
                            onChange={(e) => {
                                setTradeFrom(e.target.value);
                                if (e.target.value > tradeTo) {
                                    setTradeTo(e.target.value);
                                }
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            label="To"
                            value={tradeTo}
                            inputProps={{ min: tradeFrom }}
                            onChange={(e) => {
                                setTradeTo(e.target.value);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setTradeOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={() => void onAddTradeSchool()}>
                        Add to calendar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Pay note ──────────────────────────────────────────────── */}
            <Dialog
                open={payOpen}
                fullWidth
                maxWidth="xs"
                onClose={() => {
                    setPayOpen(false);
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Pay note</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <TextField
                            type="date"
                            label="Date"
                            value={payDate}
                            onChange={(e) => {
                                setPayDate(e.target.value);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            multiline
                            minRows={3}
                            label="Note"
                            placeholder="Anything payroll needs to know"
                            value={payNote}
                            onChange={(e) => {
                                setPayNote(e.target.value);
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setPayOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={() => void onAddPayNote()}>
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Toast
                placement="centre"
                autoHideDuration={toast?.severity === 'warning' ? 9000 : 4000}
                toast={toast}
                onClose={() => {
                    setToast(null);
                }}
            />
        </EmployeeShell>
    );
}
